import React from 'react';
import PropTypes from 'prop-types';
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser';
import { Link, withPrefix } from 'gatsby';
// import path from 'path';
import TableWithFilters from './table-with-filters';

// Font Awesome family/style classes and cosmetic modifiers that may sit
// alongside the icon-name class. Used to pick out the actual icon name.
const FA_FAMILIES = new Set([
  'fa-solid',
  'fa-regular',
  'fa-light',
  'fa-thin',
  'fa-duotone',
  'fa-brands',
  'fas',
  'far',
  'fal',
  'fat',
  'fad',
  'fab',
  'fa',
]);

// Icon names that live in the Font Awesome "Brands" font. GitBook's git-sync
// export only emits the bare name (`fa-github`), so we must route these to the
// `fa-brands` family; everything else defaults to `fa-solid`.
const FA_BRANDS = new Set([
  'github',
  'gitlab',
  'bitbucket',
  'facebook',
  'facebook-f',
  'x-twitter',
  'twitter',
  'instagram',
  'linkedin',
  'linkedin-in',
  'youtube',
  'discord',
  'slack',
  'mastodon',
  'threads',
  'tiktok',
  'whatsapp',
  'telegram',
  'reddit',
  'pinterest',
  'vimeo',
  'twitch',
  'medium',
  'stack-overflow',
  'figma',
  'dribbble',
  'behance',
  'google',
  'microsoft',
  'apple',
  'windows',
  'android',
  'linux',
  'docker',
  'npm',
  'node-js',
  'react',
  'wordpress',
  'aws',
  'chrome',
  'firefox',
  'safari',
  'edge',
]);

// remark-footnotes collects every `[^n]` footnote into a single
// `<div class="footnotes">` list at the end of the article. We leave it there —
// that is the authored position (footnote definitions live at the bottom of the
// markdown source) and it renders as an ordinary block. We only:
//   1. drop the `↩` back-reference links remark appends, so notes read as plain
//      notes like the pre-GitBook inline-HTML footnotes did;
//   2. insert a superscript ", " between consecutive footnote references, so
//      multiple refs read as "1, 2, 3" (the pre-GitBook inline-HTML footnotes
//      rendered `<sup>1, </sup>` this way). remark emits bare adjacent
//      `<sup>…</sup><sup>…` refs with no separator, which would otherwise render
//      glued as "123". The `\s*` also swallows any stray whitespace left between
//      refs in the source, so a `[^1][^2] [^3]` typo can't show up as an odd gap;
//      and
//   3. re-sort the <li> items by their numeric id, because remark emits them in
//      reference-encounter order and `[^n]` refs inside table cells can jumble
//      that, leaving the list out of step with the `[^n]` superscripts.
//
// NB: do NOT splice footnotes beneath their referencing table. When that table
// is a `table-with-filters`, the block lands inside the wrapper div, which
// TableWithFilters does not render — silently dropping the footnotes.
const normalizeFootnotes = (html) => {
  html = html.replace(
    /\s*<a\b[^>]*class="footnote-backref"[^>]*>[\s\S]*?<\/a>/g,
    ''
  );

  html = html.replace(
    /(<a[^>]*class="footnote-ref"[^>]*>[^<]*)<\/a><\/sup>\s*(?=<sup[^>]*><a[^>]*class="footnote-ref")/g,
    '$1, </a></sup>'
  );

  // Capture whether the source block carried the `<hr>` divider, and preserve
  // that choice. remark-generated footnotes come with an `<hr>` (added by the
  // transformer) and keep it; hand-written in-table footnote blocks authored
  // without one render without a divider.
  const blockRe =
    /<div class="footnotes">\s*(<hr>\s*)?<ol>\n?([\s\S]*?)\n?<\/ol>\s*<\/div>/;
  const block = html.match(blockRe);
  if (!block) return html;

  const hadHr = !!block[1];
  const items = block[2].match(/<li id="fn-?[0-9.]+">[\s\S]*?<\/li>/g);
  if (!items || !items.length) return html;

  const numOf = (li) => parseFloat((li.match(/id="fn-?([0-9.]+)"/) || [])[1]);
  const sorted = [...items].sort((a, b) => numOf(a) - numOf(b));

  return html.replace(
    blockRe,
    `<div class="footnotes">\n${hadHr ? '<hr>\n' : ''}<ol>\n${sorted.join(
      '\n'
    )}\n</ol>\n</div>`
  );
};

// GitBook exports a callout as inline HTML when a block `{% hint %}` can't be
// used (e.g. inside a table cell, where GitBook strips block callouts):
//   <div data-gb-custom-block data-tag="hint" data-style="info"><p>…</p></div>
// Map its data-style onto our callout classes so it renders identically to a
// normal {% hint %} — colour + icon + localized title. Mirrors the remark
// transformer's alert mapping (info→note, success→tip, warning→warning,
// danger→important, caution→caution).
const HINT_STYLE_MAP = {
  info: { cls: 'note', icon: 'icon-info', label: 'note' },
  success: { cls: 'tip', icon: 'icon-tip', label: 'tip' },
  warning: { cls: 'warning', icon: 'icon-warning', label: 'warning' },
  danger: { cls: 'important', icon: 'icon-warning-2', label: 'important' },
  caution: { cls: 'caution', icon: 'icon-caution', label: 'caution' },
};
// Localized callout titles (from each locale's settings.json dictionary), keyed
// by the short lang code that reaches the article body (`frontmatter.lang`).
const HINT_LABELS = {
  en: {
    note: 'Note',
    tip: 'Tip',
    warning: 'Warning',
    important: 'Important',
    caution: 'Caution',
  },
  de: {
    note: 'Hinweis',
    tip: 'Tipp',
    warning: 'Warnung',
    important: 'Wichtig',
    caution: 'Vorsicht',
  },
  da: {
    note: 'Bemærk',
    tip: 'Tip',
    warning: 'Advarsel!',
    important: 'Vigtigt',
    caution: 'Forsigtig',
  },
};

// The article HTML is rendered to React elements, server-side and again in the
// browser for hydration. If the markup is something the browser's HTML parser
// "repairs" when it reads the SSR page, the DOM no longer matches React's tree
// and hydration fails (React #418/#423), so React throws away the server HTML
// and re-renders the whole page client-side. Repair those patterns up front,
// the same way the browser would, so both sides agree:
//   - nested links: raw `<a href="URL">URL</a>` in a table cell gets its bare
//     URL text autolinked by remark-gfm, giving `<a><a>…</a></a>`. Keep only the
//     outer link.
//   - `<br>` / whitespace directly inside table/thead/tbody/tfoot/tr: GitBook
//     turns line breaks in raw-HTML tables inside table cells into `<br>`, which
//     the browser moves out of the table. Drop them.
//   - `<tr>` directly under `<table>`: the browser inserts an implicit `<tbody>`.
//     Insert it explicitly.
const TABLE_STRUCTURE = new Set(['table', 'thead', 'tbody', 'tfoot', 'tr']);

const wrapBareRows = (table) => {
  const children = [];
  let tbody = null;
  table.children.forEach((child) => {
    if (child.type === 'tag' && child.name === 'tr') {
      if (!tbody) {
        tbody = {
          type: 'tag',
          name: 'tbody',
          attribs: {},
          children: [],
          parent: table,
        };
        children.push(tbody);
      }
      child.parent = tbody;
      tbody.children.push(child);
    } else {
      tbody = null;
      children.push(child);
    }
  });
  return children;
};

const repairNodes = (nodes, parent = null, insideLink = false) => {
  const out = [];
  nodes.forEach((node) => {
    if (node.type !== 'tag') {
      out.push(node);
      return;
    }
    if (insideLink && node.name === 'a') {
      out.push(...repairNodes(node.children || [], parent, true));
      return;
    }
    node.children = repairNodes(
      node.children || [],
      node,
      insideLink || node.name === 'a'
    );
    if (TABLE_STRUCTURE.has(node.name)) {
      node.children = node.children.filter(
        (c) =>
          !(c.type === 'tag' && c.name === 'br') &&
          !(c.type === 'text' && !c.data.trim())
      );
    }
    if (node.name === 'table') node.children = wrapBareRows(node);
    out.push(node);
  });
  out.forEach((n) => {
    n.parent = parent;
  });
  return out;
};

const HTMLParser = ({
  html: rawHtml,
  lang,
  // pages, fileDirectory, prefix
}) => {
  const html = normalizeFootnotes(rawHtml.split('<p>[GREY-SECTION]</p>')[0]);
  // Transformer for React HTML Parser
  const transform = (node, index) => {
    // GitBook inline icons: git-sync exports them as
    // `<i class="fa-check">:check:</i>` — a Font Awesome icon-name class plus a
    // `:name:` text placeholder. Render the real icon: inject the required
    // Font Awesome family class (solid by default, brands for logos/social),
    // which the export omits, and drop the `:name:` text so it isn't shown.
    // Only touches `<i>` tags that carry an `fa-*` class, so existing markup
    // like `<i class="icon icon-info">` in callouts is left untouched.
    if (
      node.type === 'tag' &&
      node.name === 'i' &&
      /(^|\s)fa-[a-z0-9-]+/i.test(node.attribs.class || '')
    ) {
      const classes = (node.attribs.class || '').split(/\s+/).filter(Boolean);
      const hasFamily = classes.some((c) => FA_FAMILIES.has(c));
      const iconName = (
        classes.find((c) => c.startsWith('fa-') && !FA_FAMILIES.has(c)) || ''
      ).replace(/^fa-/, '');
      let family = null;
      if (!hasFamily)
        family = FA_BRANDS.has(iconName) ? 'fa-brands' : 'fa-solid';
      const className = [family, ...classes].filter(Boolean).join(' ');
      // Render on a <span>, not <i>: the site's global `i { font-family:
      // 'continia-icons' !important }` rule (src/sass/_icons.scss) would
      // otherwise override Font Awesome's font and blank the glyph. FA's
      // selectors are class-based, so a <span> renders identically.
      return <span key={index} className={className} aria-hidden="true" />;
    }

    // Eksterne links
    if (
      node.type === 'tag' &&
      node.name === 'a' &&
      node.attribs.href &&
      ((node.attribs.href || '').startsWith('http://') ||
        (node.attribs.href || '').startsWith('https://'))
    ) {
      return (
        <a
          href={node.attribs.href}
          key={index}
          {...node.attribs}
          target="_blank"
          rel="noreferrer noopener"
        >
          {node.children.map((n, i) => convertNodeToElement(n, i, transform))}
        </a>
      );
    }

    // File download links — render as plain <a> so the browser handles the
    // download attribute instead of Gatsby's router treating it as a route.
    if (
      node.type === 'tag' &&
      node.name === 'a' &&
      node.attribs.href &&
      node.attribs.download !== undefined
    ) {
      if (node.attribs.class) {
        node.attribs.className = node.attribs.class;
        delete node.attribs.class;
      }

      // Strip the language prefix (matching the <Link> path) and re-add the
      // current Gatsby pathPrefix via withPrefix — empty in dev, "/en-us" etc.
      // in prod — so the href works the same in both environments.
      let bare = node.attribs.href;
      if (bare.startsWith('/en-us/')) bare = bare.replace('/en-us/', '/');
      else if (bare.startsWith('/da-dk/')) bare = bare.replace('/da-dk/', '/');
      else if (bare.startsWith('/de-de/')) bare = bare.replace('/de-de/', '/');

      const { href: _, ...rest } = node.attribs;
      return (
        <a href={withPrefix(bare)} key={index} {...rest}>
          {node.children.map((n, i) => convertNodeToElement(n, i, transform))}
        </a>
      );
    }

    // Interne Links
    if (
      node.type === 'tag' &&
      node.name === 'a' &&
      node.attribs.href &&
      (node.attribs['aria-label'] || '').indexOf('permalink') === -1 &&
      (node.attribs.href || '').indexOf('http://') === -1 &&
      (node.attribs.href || '').indexOf('https://') === -1 &&
      (node.attribs.href || '').indexOf('mailto:') === -1 &&
      (node.attribs.href || '').indexOf('tel:') === -1 &&
      (node.attribs.href || '').indexOf('#') !== 0 &&
      !node.attribs.href.startsWith('/images/')
    ) {
      if (node.attribs && node.attribs.class) {
        node.attribs.className = node.attribs.class;
        delete node.attribs.class;
      }
      // console.log('@ intern link', node.attribs.href);

      let to = node.attribs.href.split('#')[0];
      to = (to || '').trim();
      // console.log('/to', to);
      const hash = node.attribs.href.split('#')[1];
      delete node.attribs.href;

      if (to.endsWith('.md')) {
        // throw new Error('Link til .md fil =>', to);
      }
      // if (to.substr(to.length - 3).toLowerCase() === '.md') {
      //   const toFilePath = decodeURIComponent(to)
      //     .trim()
      //     .replace(`/${prefix}/`, '/');

      //   const relativePagePath = path.normalize(
      //     `${fileDirectory}/${toFilePath}`
      //   );

      //   const page = pages.find(
      //     (p) =>
      //       p.filePath.toLowerCase() === toFilePath.toLowerCase() ||
      //       p.filePath.toLowerCase() === relativePagePath.toLowerCase()
      //   );

      //   if (page) {
      //     console.log('@@ html - page found', page);

      //     to = page.path;
      //     if (node.children.length === 0) {
      //       node.children.push({
      //         data: page.title,
      //         next: null,
      //         parent: node,
      //         prev: null,
      //         type: 'text',
      //       });
      //     }
      //   } else {
      //     console.log('//Error: Page for MD link not found:', {
      //       to,
      //       relativePagePath,
      //       fileDirectory,
      //       prefix,
      //       toFilePath,
      //       toDecoded: decodeURIComponent(to),
      //       pages,
      //     });
      //     return null;
      //   }
      // }

      if (to.startsWith('/en-us/en-us/')) {
        to = to.replace('/en-us/en-us/', '/en-us/');
      }
      if (to.startsWith('/da-dk/da-dk/')) {
        to = to.replace('/da-dk/da-dk/', '/da-dk/');
      }
      if (to.startsWith('/de-de/de-de/')) {
        to = to.replace('/de-de/de-de/', '/de-de/');
      }
      if (to.startsWith('/en-us/')) {
        to = to.replace('/en-us/', '/');
      }
      if (to.startsWith('/da-dk/')) {
        to = to.replace('/da-dk/', '/');
      }
      if (to.startsWith('/de-de/')) {
        to = to.replace('/de-de/', '/');
      }

      return (
        <Link
          to={`${to}${hash ? `#${hash} ` : ''}`}
          key={index}
          {...node.attribs}
        >
          {node.children.map((n, i) => convertNodeToElement(n, i, transform))}
        </Link>
      );
    }

    // Helper function to get locale from lang
    const getLocale = (language) => {
      if (!language) return 'en-US';
      const langLower = language.toLowerCase();
      if (langLower === 'da' || langLower === 'da-dk') return 'da-DK';
      if (langLower === 'de' || langLower === 'de-de') return 'de-DE';
      if (langLower === 'en' || langLower === 'en-us') return 'en-US';
      if (langLower.length === 2) {
        return `${langLower}-${langLower.toUpperCase()}`;
      }
      return language;
    };

    // Helper function to sort a table node
    const sortTableNode = (tableNode, shouldSort, columnIndex = 1) => {
      if (!shouldSort || !lang) return tableNode;

      const locale = getLocale(lang);
      // Convert to 0-based index (1 = first column = index 0)
      const sortColumnIndex = Math.max(0, columnIndex - 1);

      // Clone the node to avoid mutation
      const processedNode = { ...tableNode, children: [...tableNode.children] };

      // Find tbody and sort its rows
      const tbodyIndex = processedNode.children.findIndex(
        (child) => child.type === 'tag' && child.name === 'tbody'
      );

      if (tbodyIndex !== -1) {
        const tbody = processedNode.children[tbodyIndex];
        const rows = tbody.children.filter(
          (child) => child.type === 'tag' && child.name === 'tr'
        );

        // Sort rows based on specified column
        const sortedRows = [...rows].sort((a, b) => {
          const getCellText = (row, colIndex) => {
            const cells = row.children.filter(
              (child) =>
                child.type === 'tag' &&
                (child.name === 'td' || child.name === 'th')
            );
            if (cells.length === 0 || colIndex >= cells.length) return '';

            const targetCell = cells[colIndex];
            let text = '';

            const extractText = (node) => {
              if (node.type === 'text') {
                text += node.data;
              } else if (node.children) {
                node.children.forEach(extractText);
              }
            };

            extractText(targetCell);
            return text.trim();
          };

          const textA = getCellText(a, sortColumnIndex);
          const textB = getCellText(b, sortColumnIndex);

          return textA.localeCompare(textB, locale, { sensitivity: 'base' });
        });

        // Replace tbody with sorted version
        processedNode.children[tbodyIndex] = {
          ...tbody,
          children: sortedRows,
        };
      }

      return processedNode;
    };

    // GitBook hint exported as inline HTML (survives table cells) — render it as
    // our normal callout so it matches a block {% hint %} exactly.
    if (
      node.type === 'tag' &&
      node.name === 'div' &&
      node.attribs &&
      node.attribs['data-gb-custom-block'] !== undefined &&
      node.attribs['data-tag'] === 'hint'
    ) {
      const style = node.attribs['data-style'] || 'info';
      const { cls, icon, label } = HINT_STYLE_MAP[style] || HINT_STYLE_MAP.info;
      const labels = HINT_LABELS[lang] || HINT_LABELS.en;
      return (
        <div key={index} className={`alarm alarm-${cls}`}>
          <p className="alert-title">
            <i className={`icon ${icon}`} />
            <span>{labels[label]}</span>
          </p>
          {(node.children || []).map((n, i) =>
            convertNodeToElement(n, i, transform)
          )}
        </div>
      );
    }

    // Check for table-with-filters first (before regular tables)
    if (node.attribs?.class?.includes('table-with-filters')) {
      const shouldSort = node.attribs['data-alphabetical-sort'] !== undefined;
      const columnIndex = node.attribs['data-alphabetical-sort']
        ? parseInt(node.attribs['data-alphabetical-sort'], 10) || 1
        : 1;

      // If we need to sort, find the table inside and sort it
      let processedNode = node;
      if (shouldSort) {
        processedNode = { ...node, children: [...node.children] };
        const tableIndex = processedNode.children.findIndex(
          (child) => child.type === 'tag' && child.name === 'table'
        );

        if (tableIndex !== -1) {
          const tableNode = processedNode.children[tableIndex];
          processedNode.children[tableIndex] = sortTableNode(
            tableNode,
            true,
            columnIndex
          );
        }
      }

      return (
        <TableWithFilters
          node={processedNode}
          transform={transform}
          index={index}
        />
      );
    }

    // Check for div wrapper with data-alphabetical-sort (but not table-with-filters)
    if (
      node.type === 'tag' &&
      node.name === 'div' &&
      node.attribs['data-alphabetical-sort'] !== undefined
    ) {
      const columnIndex = node.attribs['data-alphabetical-sort']
        ? parseInt(node.attribs['data-alphabetical-sort'], 10) || 1
        : 1;

      // Find the table inside and sort it
      const processedNode = { ...node, children: [...node.children] };
      const tableIndex = processedNode.children.findIndex(
        (child) => child.type === 'tag' && child.name === 'table'
      );

      if (tableIndex !== -1) {
        const tableNode = processedNode.children[tableIndex];
        processedNode.children[tableIndex] = sortTableNode(
          tableNode,
          true,
          columnIndex
        );
      }

      // Return the div wrapper with the sorted table inside
      return (
        <div key={index} {...node.attribs}>
          {processedNode.children.map((n, i) =>
            convertNodeToElement(n, i, transform)
          )}
        </div>
      );
    }

    // Regular tables
    if (node.type === 'tag' && node.name === 'table') {
      const shouldSort = node.attribs['data-alphabetical-sort'] !== undefined;
      const columnIndex = node.attribs['data-alphabetical-sort']
        ? parseInt(node.attribs['data-alphabetical-sort'], 10) || 1
        : 1;
      const processedNode = sortTableNode(node, shouldSort, columnIndex);

      return (
        <div key={index} className="tbl table-scroll-wrapper">
          <table className="table" {...node.attribs}>
            {processedNode.children.map((n, i) => {
              const elem = convertNodeToElement(n, i, transform);
              // Remove weird whitespace linebreaks inside table (illegal)
              if (typeof elem === 'string' && elem.trim() === '') {
                return null;
              }
              return elem;
            })}
          </table>
        </div>
      );
    }

    // Svg bugs
    if (node.type === 'tag' && node.name === 'svg') {
      const { width, height, viewbox, preserveaspectratio, ...others } =
        node.attribs;
      return (
        <svg
          key={index}
          width={width}
          height={height}
          viewBox={viewbox}
          preserveAspectRatio={preserveaspectratio}
          {...others}
        >
          {node.children.map((n, i) => {
            if (
              n.attribs['fill-rule'] &&
              n.type === 'tag' &&
              n.name === 'path'
            ) {
              const { 'fill-rule': fillRule, ...otherAttr } = n.attribs;

              return <path key={i} fillRule={fillRule} {...otherAttr} />;
            }
            return convertNodeToElement(n, i, transform);
          })}
        </svg>
      );
    }

    // Images
    // if (node.type === 'tag' && node.name === 'img') {
    //   // node.attribs.src = node.attribs.src;
    //   return <img key={index} {...node.attribs} />;
    // }
  };

  return ReactHtmlParser(html, {
    transform,
    preprocessNodes: (nodes) => repairNodes(nodes),
    decodeEntities: true,
  });
};

HTMLParser.propTypes = {
  html: PropTypes.string.isRequired,
  lang: PropTypes.string,
  // pages: PropTypes.array.isRequired,
};
HTMLParser.defaultProps = {};

export default HTMLParser;
